import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCinematicSlides } from '../hooks/useCinematicSlides';
import AppNav from '../components/common/AppNav';
import { hasSeenSlideshow, markSlideshowSeen } from '../utils/seenState';
import '../styles/public.css';

const AFRICA_ACCENT = '#22c55e';

const AfricaPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  // Returning visitors (within 7 days) land on the last/CTA slide directly.
  const seen = hasSeenSlideshow();
  const { current, slideClass, advance, retreat, goTo } = useCinematicSlides(3, 5800, 2, seen ? 2 : 0);

  useEffect(() => {
    if (!seen) markSlideshowSeen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') advance();
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   retreat();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [advance, retreat]);

  return (
    <div className="cin-shell">
      <AppNav accentColor={AFRICA_ACCENT} />

      {/* ── Slide 1: Hero ────────────────────────────────── */}
      <div className={slideClass(0)}>
        <div className="cin-bg cin-bg--africa">
          <div className="cin-stars" />
          <div className="cin-orb cin-orb--africa" />
          <div className="cin-overlay" />
        </div>
        <div className="cin-content" style={{ paddingTop: 60 }}>
          <div className="cin-inner">
            <span className="cin-label cin-label--green">{t('africaPage.slide1Label')}</span>
            <h1 className="cin-headline">
              {t('africaPage.slide1Headline')}
            </h1>
            <p className="cin-subline">
              {t('africaPage.slide1Sub')}
            </p>
            <div className="cin-gap--sm" />
            <button className="cin-action-btn cin-action-btn--green" onClick={advance}>
              {t('africaPage.slide1Cta')} &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {/* ── Slide 2: What we offer ───────────────────────── */}
      <div className={slideClass(1)}>
        <div className="cin-bg cin-bg--africa">
          <div className="cin-stars" />
          <div className="cin-overlay cin-overlay--heavy" />
        </div>
        <div className="cin-content" style={{ paddingTop: 60 }}>
          <div className="cin-inner">
            <span className="cin-label cin-label--green">{t('africaPage.slide2Label')}</span>
            <h1 className="cin-headline">
              {t('africaPage.slide2Headline')}
            </h1>
            <p className="cin-subline">
              {t('africaPage.slide2Sub')}
            </p>
            <div className="cin-gap--sm" />
            <div className="cin-stat-row">
              <div className="cin-stat">
                <div className="cin-stat-num" style={{ color: '#22c55e' }}>{t('africaPage.slide2Stat1Value')}</div>
                <div className="cin-stat-label">{t('africaPage.slide2Stat1Label')}</div>
              </div>
              <div className="cin-stat">
                <div className="cin-stat-num" style={{ color: '#22c55e' }}>{t('africaPage.slide2Stat2Value')}</div>
                <div className="cin-stat-label">{t('africaPage.slide2Stat2Label')}</div>
              </div>
              <div className="cin-stat">
                <div className="cin-stat-num" style={{ color: '#22c55e' }}>{t('africaPage.slide2Stat3Value')}</div>
                <div className="cin-stat-label">{t('africaPage.slide2Stat3Label')}</div>
              </div>
            </div>
            <div className="cin-gap--sm" />
            <button className="cin-action-btn cin-action-btn--ghost" style={{ borderColor: 'rgba(34,197,94,0.4)', color: '#22c55e' }} onClick={advance}>
              {t('africaPage.slide2Continue')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Slide 3: CTA ─────────────────────────────────── */}
      <div className={slideClass(2)}>
        <div className="cin-bg cin-bg--africa">
          <div className="cin-stars" />
          <div className="cin-overlay" style={{ background: 'rgba(5,6,13,0.35)' }} />
        </div>
        <div className="cin-content" style={{ paddingTop: 60 }}>
          <div className="cin-inner">
            <span className="cin-label cin-label--green">{t('africaPage.slide3Label')}</span>
            <h1 className="cin-headline">{t('africaPage.slide3Headline')}</h1>
            <p className="cin-subline">
              {t('africaPage.slide3Sub')}
            </p>
            <div className="cin-gap--sm" />
            <div className="cin-action-row">
              <button
                className="cin-action-btn cin-action-btn--green"
                onClick={() => navigate('/globe?region=africa')}
              >
                🌍 {t('africaPage.slide3Globe')}
              </button>
              <Link to="/shop" className="cin-action-btn cin-action-btn--ghost">
                🛍️ {t('africaPage.slide3Browse')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Controls ─────────────────────────────────────── */}
      <div className="cin-controls">
        <button
          className={`cin-arrow cin-arrow--left${current === 0 ? ' cin-arrow--hidden' : ''}`}
          onClick={retreat}
          aria-label="Previous"
        >
          ‹
        </button>
        <button
          className={`cin-arrow cin-arrow--right${current === 2 ? ' cin-arrow--hidden' : ''}`}
          onClick={advance}
          aria-label="Next"
        >
          ›
        </button>
        <div className="cin-dots">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              className={`cin-dot${current === i ? ' cin-dot--active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AfricaPage;
